import React, { useState, useEffect } from "react";
import api from "../services/api";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Input, Alert, Table } from "reactstrap";
import { FaTrash } from "react-icons/fa"; // Import delete icon


const EmpRequest = ({businessId}) => {
  const [leaveType, setLeaveType] = useState("Single");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [requestType, setRequestType] = useState("Paid");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { businessId: businessIdFromParams } = useParams(); // Get from URL (if exists)
  const bus_id = businessId || businessIdFromParams;
  const [employee, setEmployee] = useState({ id: "", first_name: "", last_name: "" });

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await api.post("/employee/profile");
        setEmployee(response.data); // Store the employee data in state
      } catch (error) {
        setError("Failed to fetch employee data.");
      }
    };
  
    fetchEmployeeData();
  }, []);


  // Fetch leave requests for the employee
  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const response = await api.get(`/employee/leave-requests`);
        setLeaveRequests(response.data);
      } catch (error) {
        setError("Failed to fetch leave requests.");
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveRequests();
  }, []); // Empty dependency array ensures it runs only once when the component mounts

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate || (leaveType === "Multiple" && !endDate)) {
      setError("Please fill all required fields.");
      return;
    }

    if (leaveType === "Half" && (!startTime || !endTime)) {
      setError("Please provide start and end times for half-day leave.");
      return;
    }

    try {
      const response = await api.post("/leave-request", {
        business_id: bus_id,
        department_id : employee.department_id,
        first_name: employee.first_name,
        last_name: employee.last_name,
        leave_type: leaveType,
        start_date: startDate,
        end_date: endDate || null,
        request_type: requestType,
        reason: reason || null,
        start_time: leaveType === "Half" ? startTime : null,
        end_time: leaveType === "Half" ? calculatedEndTime : null,
      });

      if (response.status === 200) {
        setSuccess("Leave request submitted successfully.");
        setError(null);

        // ✅ Reset form fields after submission
        setLeaveType("Single");
        setStartDate("");
        setEndDate("");
        setStartTime("");
        setEndTime("");
        setRequestType("Paid");
        setReason("");

        // ✅ Refresh the leave requests list after submission
        const updatedRequests = await api.get(`/employee/leave-requests`);
        setLeaveRequests(updatedRequests.data);
      } else {
        setError(response.data?.error || "Failed to submit leave request.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Error submitting request. Please try again.");
    }
  };


  const calculateEndTime = (startTime) => {
    if (!startTime) return "";
  
    let [hours, minutes] = startTime.split(":").map(Number);
    hours += 4; // Add 4 hours to Start Time
  
    // Handle cases where the time goes past midnight
    if (hours >= 24) {
      hours -= 24;
    }
  
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };
  
  // Function for Displaying End Time in 12-Hour Format
  const formatEndTimeForDisplay = (endTime) => {
    if (!endTime) return "";
  
    let [hours, minutes] = endTime.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12; // Convert 0 to 12 for AM/PM format
  
    return `${formattedHours}:${String(minutes).padStart(2, "0")} ${period}`;
  };
  
  
  const calculatedEndTime = calculateEndTime(startTime);

  // Handle leave request deletion
  const handleDelete = async (id) => {
    try {
      await api.delete(`/leave-request/${id}`); // Call delete API
      setLeaveRequests(leaveRequests.filter((request) => request.id !== id)); // Update state
      setSuccess("Leave request deleted successfully.");
    } catch (err) {
      setError("Failed to delete leave request.");
    }
  };

  return (
    <Container className="mt-6">
  <Row>
    {/* Left Container: Leave Request Form */}
    <Col lg="4" md="4" sm="12"> 
    <Container className="p-0">
      <Card className="shadow-lg">
        <CardBody>
          <h5 className="text-center mb-3">Request Time Off</h5>
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Leave Type</Label>
              <Input type="select" bsSize="sm" value={leaveType} onChange={(e) => {
                setLeaveType(e.target.value);
                if (e.target.value !== "Multiple") setEndDate("");
              }}>
                <option value="Single">Single Day</option>
                <option value="Half">Half Day (4 hours)</option>
                <option value="Multiple">Multiple Days</option>
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Start Date</Label>
              <Input type="date" bsSize="sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </FormGroup>

            {leaveType === "Multiple" && (
              <FormGroup>
                <Label>End Date</Label>
                <Input type="date" bsSize="sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
              </FormGroup>
            )}

            {leaveType === "Half" && (
              <>
              <FormGroup>
              <Label>Start Time</Label>
              <Input
                type="time"
                bsSize="sm"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setEndTime(calculateEndTime(e.target.value)); // Auto-update endTime state
                }}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>End Time (Auto-Calculated)</Label>
              <Input
                type="text"
                bsSize="sm"
                value={startTime ? formatEndTimeForDisplay(endTime) : ""}
                readOnly
              />
            </FormGroup>


              </>
            )}


            <FormGroup>
              <Label>Request Type</Label>
              <Input type="select" bsSize="sm" value={requestType} onChange={(e) => setRequestType(e.target.value)}>
                <option value="Paid">Paid Leave</option>
                <option value="Unpaid">Unpaid Leave</option>
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Reason (Optional)</Label>
              <Input type="textarea" bsSize="sm" value={reason} onChange={(e) => setReason(e.target.value)} />
            </FormGroup>

            <Button color="primary" block size="sm" type="submit">
              Submit Request
            </Button>
          </Form>
        </CardBody>
      </Card>
    </Container>
  </Col>

    {/* Right Container: Leave Requests Table */}
    <Col lg="8" md="6" sm="12">
      <Container className="p-0">
        <Card className="shadow-lg">
          <CardBody>
            <h4 className="text-center mb-4">Leave Requests</h4>

            {loading ? (
              <p className="text-center">Loading leave requests...</p>
            ) : leaveRequests.length > 0 ? (
              <Table striped bordered responsive className="mt-3">
                <thead>
                  <tr>
                    <th>Leave Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Request Type</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.leave_type}</td>
                      <td>{new Date(request.start_date).toLocaleDateString()}</td>
                      <td>{new Date(request.end_date).toLocaleDateString()}</td>
                      <td>{request.request_type}</td>
                      <td>{request.status}</td>
                      <td>
                        {request.status === "Pending" && (
                          <Button color="danger" size="sm" onClick={() => handleDelete(request.id)}>
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted text-center mt-3">No leave requests available.</p>
            )}
          </CardBody>
        </Card>
      </Container>
    </Col>
  </Row>
</Container>


  );
};

export default EmpRequest;






// import React, { useState } from "react";
// import api from "../services/api";
// import { useParams } from "react-router-dom";
// import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Input, Alert } from "reactstrap";



// const EmpRequest = () => {
//   const { businessId: bus_id } = useParams();
//   const [leaveType, setLeaveType] = useState("Single");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [requestType, setRequestType] = useState("Paid");
//   const [reason, setReason] = useState("");
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);
//   const [startTime, setStartTime] = useState(""); // ✅ Add state for start time
//   const [endTime, setEndTime] = useState("");
//   const [leaveRequests, setLeaveRequests] = useState([]);


//   // Fetch leave requests for the business
//   useEffect(() => {
//     const fetchLeaveRequests = async () => {
//       try {
//         const response = await api.get(`/employee/leave-requests`);
//         setLeaveRequests(response.data);
//       } catch (error) {
//         setError("Failed to fetch leave requests.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchLeaveRequests();
//   });


//   const handleSubmit = async (e) => {
//     e.preventDefault();
  
//     if (!startDate || (leaveType === "Multiple" && !endDate)) {
//       setError("Please fill all required fields.");
//       return;
//     }
  
//     if (leaveType === "Half" && (!startTime || !endTime)) {
//       setError("Please provide start and end times for half-day leave.");
//       return;
//     }
  
//     try {
//       const response = await api.post("/leave-request", {
//         business_id: bus_id,
//         leave_type: leaveType, 
//         start_date: startDate,
//         end_date: leaveType === "Multiple" ? endDate : startDate, // ✅ Single & Half should have same Start & End
//         request_type: requestType,
//         reason: reason || null,
//         start_time: leaveType === "Half" ? startTime : null, // ✅ Send only if "Half"
//         end_time: leaveType === "Half" ? endTime : null, // ✅ Send only if "Half"
//       });
  
//       if (response.status === 200) {
//         setSuccess("Leave request submitted successfully.");
//         setError(null);
  
//         // ✅ Reset form fields after submission
//         setLeaveType("Single");
//         setStartDate("");
//         setEndDate("");
//         setStartTime("");  // ✅ Clear start time
//         setEndTime("");    // ✅ Clear end time
//         setRequestType("Paid");
//         setReason("");
//       } else {
//         setError(response.data?.error || "Failed to submit leave request.");
//       }
//     } catch (err) {
//       setError(err.response?.data?.error || "Error submitting request. Please try again.");
//     }
//   };
  

//   return (
//     <Container className="mt-5">
//       <Row className="justify-content-center">
//         <Col lg="6">
//           <Card className="shadow-lg">
//             <CardBody>
//               <h3 className="text-center mb-4">Request Time Off</h3>
              
//               {error && <Alert color="danger">{error}</Alert>}
//               {success && <Alert color="success">{success}</Alert>}

//               <Form onSubmit={handleSubmit}>
//               <FormGroup>
//                 <Label>Leave Type</Label>
//                 <Input 
//                   type="select" 
//                   value={leaveType} 
//                   onChange={(e) => {
//                     setLeaveType(e.target.value);
//                     if (e.target.value !== "Multiple") setEndDate(""); // Clear end date for Single & Half
//                   }}
//                 >
//                   <option value="Single">Single Day</option>
//                   <option value="Half">Half Day</option>  {/* ✅ Added Half Day option */}
//                   <option value="Multiple">Multiple Days</option>
//                 </Input>
//               </FormGroup>


//                 <FormGroup>
//                   <Label>Start Date</Label>
//                   <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
//                 </FormGroup>

//                 {leaveType === "Multiple" && (
//                   <FormGroup>
//                     <Label>End Date</Label>
//                     <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
//                   </FormGroup>
//                 )}

//                 {leaveType === "Half" && (
//                   <>
//                     <FormGroup>
//                       <Label>Start Time</Label>
//                       <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
//                     </FormGroup>

//                     <FormGroup>
//                       <Label>End Time</Label>
//                       <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
//                     </FormGroup>
//                   </>
//                 )}


//                 <FormGroup>
//                   <Label>Request Type</Label>
//                   <Input type="select" value={requestType} onChange={(e) => setRequestType(e.target.value)}>
//                     <option value="Paid">Paid Leave</option>
//                     <option value="Unpaid">Unpaid Leave</option>
//                   </Input>
//                 </FormGroup>

//                 <FormGroup>
//                   <Label>Reason (Optional)</Label>
//                   <Input type="textarea" value={reason} onChange={(e) => setReason(e.target.value)} />
//                 </FormGroup>

//                 <Button color="primary" block type="submit">
//                   Submit Request
//                 </Button>
//               </Form>
//             </CardBody>
//           </Card>
//         </Col>
//       </Row>
    
//       <Row className="justify-content-center">
//         <Col lg="10">
//           <Card className="shadow-lg">
//             <CardBody>
//               <h3 className="text-center mb-4">Leave Requests</h3>

//               {error && <Alert color="danger">{error}</Alert>}
//               {success && <Alert color="success">{success}</Alert>}

//               {leaveRequests.length > 0 ? (
//                 <Table striped bordered responsive className="mt-3">
//                   <thead>
//                     <tr>
//                       <th>Employee ID</th>
//                       <th>Leave Type</th>
//                       <th>Start Date</th>
//                       <th>End Date</th>
//                       <th>Request Type</th>
//                       <th>Status</th>
//                       <th>Action</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {leaveRequests.map((request) => (
//                       <tr key={request.id}>
//                         <td>{request.employee_id}</td>
//                         <td>{request.leave_type}</td>
//                         <td>{new Date(request.start_date).toLocaleDateString()}</td>
//                         <td>{new Date(request.end_date).toLocaleDateString()}</td>
//                         <td>{request.request_type}</td>
//                         <td>{request.status}</td>
//                         <td>
//                           {request.status === "Pending" ? (
//                             <>                              
//                             </>
//                           ) : (
//                             request.status
//                           )}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </Table>
//               ) : (
//                 <p className="text-muted text-center mt-3">No leave requests available.</p>
//               )}
//             </CardBody>
//           </Card>
//         </Col>
//       </Row>
//     </Container>
  

//   );
// };

// export default EmpRequest;






// import React, { useState } from "react";
// import api from "../services/api";
// import { useParams } from "react-router-dom";
// import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Input, Alert } from "reactstrap";

// const EmpRequest = () => {
//     const { businessId: bus_id } = useParams();
//   const [leaveType, setLeaveType] = useState("Single");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [requestType, setRequestType] = useState("Paid");
//   const [reason, setReason] = useState("");
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!startDate || (leaveType === "Multiple" && !endDate)) {
//       setError("Please fill all required fields.");
//       return;
//     }

//     try {
//       const response = await api.post("/leave-request", {
//         business_id: bus_id,
//         leave_type: leaveType,
//         start_date: startDate,
//         end_date: leaveType === "Single" ? startDate : endDate,
//         request_type: requestType,
//         reason: reason,
//       });

//       if (response.status === 200) {
//         setSuccess("Leave request submitted successfully.");
//         setError(null);
//       } else {
//         setError("Failed to submit leave request.");
//       }
//     } catch (err) {
//       setError("Error submitting request. Please try again.");
//     }
//   };

//   return (
//     <Container className="mt-5">
//       <Row className="justify-content-center">
//         <Col lg="6">
//           <Card className="shadow-lg">
//             <CardBody>
//               <h3 className="text-center mb-4">Request Time Off</h3>
              
//               {error && <Alert color="danger">{error}</Alert>}
//               {success && <Alert color="success">{success}</Alert>}

//               <Form onSubmit={handleSubmit}>
//                 <FormGroup>
//                   <Label>Leave Type</Label>
//                   <Input type="select" value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
//                     <option value="Single">Single Day</option>
//                     <option value="Multiple">Multiple Days</option>
//                   </Input>
//                 </FormGroup>

//                 <FormGroup>
//                   <Label>Start Date</Label>
//                   <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
//                 </FormGroup>

//                 {leaveType === "Multiple" && (
//                   <FormGroup>
//                     <Label>End Date</Label>
//                     <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
//                   </FormGroup>
//                 )}

//                 <FormGroup>
//                   <Label>Request Type</Label>
//                   <Input type="select" value={requestType} onChange={(e) => setRequestType(e.target.value)}>
//                     <option value="Paid">Paid Leave</option>
//                     <option value="Unpaid">Unpaid Leave</option>
//                   </Input>
//                 </FormGroup>

//                 <FormGroup>
//                   <Label>Reason (Optional)</Label>
//                   <Input type="textarea" value={reason} onChange={(e) => setReason(e.target.value)} />
//                 </FormGroup>

//                 <Button color="primary" block type="submit">
//                   Submit Request
//                 </Button>
//               </Form>
//             </CardBody>
//           </Card>
//         </Col>
//       </Row>
//     </Container>
//   );
// };

// export default EmpRequest;
